import React, { useState, useEffect, useRef } from 'react';
import SectionAgent from './SectionAgent';
import axios from 'axios';
import { toast } from 'sonner';
import {
  StickyNote, Plus, Mic, MicOff, Search, Download, Trash2,
  X, ChevronDown, Tag, Pencil, Check, Star, Filter
} from 'lucide-react';
import AIAgentModule from './AIAgentModule';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const NOTE_CATEGORIES = [
  { id: 'Todas', label: 'Todas', emoji: '📋' },
  { id: 'General', label: 'General', emoji: '📝' },
  { id: 'Personal', label: 'Personal', emoji: '👤' },
  { id: 'Trabajo', label: 'Trabajo', emoji: '💼' },
  { id: 'Ideas', label: 'Ideas', emoji: '💡' },
  { id: 'Proyectos', label: 'Proyectos', emoji: '🚀' },
  { id: 'Salud', label: 'Salud', emoji: '❤️' },
  { id: 'Finanzas', label: 'Finanzas', emoji: '💰' },
  { id: 'Recetas', label: 'Recetas', emoji: '🍳' },
  { id: 'Viajes', label: 'Viajes', emoji: '✈️' },
];

const COLORS = [
  '#1e293b', '#1e1b4b', '#1a2e1a', '#2d1b1b', '#1a2340', '#2d2200'
];

const NotesModule = ({ user }) => {
  const [notes, setNotes] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Todas');
  const [showCreate, setShowCreate] = useState(false);
  const [editNote, setEditNote] = useState(null);
  const [listening, setListening] = useState(false);
  const [listeningField, setListeningField] = useState('');
  const [loading, setLoading] = useState(false);
  const [newNote, setNewNote] = useState({ title: '', content: '', category: 'General', color: COLORS[0], starred: false });
  const recognitionRef = useRef(null);

  useEffect(() => {
    loadNotes();
    setupRecognition();
  }, [user.id]);

  const setupRecognition = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const r = new SR();
    r.lang = 'es-MX';
    r.continuous = false;
    r.onresult = (e) => {
      const txt = e.results[0][0].transcript;
      if (listeningField === 'title') {
        setNewNote(prev => ({ ...prev, title: txt }));
      } else {
        setNewNote(prev => ({ ...prev, content: prev.content + (prev.content ? ' ' : '') + txt }));
      }
      setListening(false);
    };
    r.onerror = r.onend = () => setListening(false);
    recognitionRef.current = r;
  };

  const loadNotes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notes/${user.id}`);
      setNotes(res.data || []);
    } catch (e) {}
    finally { setLoading(false); }
  };

  const handleVoice = (field) => {
    if (!recognitionRef.current) { toast.error('Voz no disponible en este navegador'); return; }
    setListeningField(field);
    if (listening) { recognitionRef.current.stop(); return; }
    recognitionRef.current.start();
    setListening(true);
  };

  const saveNote = async () => {
    if (!newNote.title.trim()) { toast.error('Agrega un título'); return; }
    try {
      if (editNote) {
        await axios.put(`${API}/notes/${editNote.id}`, { ...newNote });
        toast.success('Nota actualizada');
      } else {
        await axios.post(`${API}/notes`, { ...newNote, user_id: user.id, is_voice: false });
        toast.success('Nota guardada');
      }
      setNewNote({ title: '', content: '', category: 'General', color: COLORS[0], starred: false });
      setShowCreate(false);
      setEditNote(null);
      loadNotes();
    } catch (e) { toast.error('Error al guardar'); }
  };

  const deleteNote = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('¿Eliminar esta nota?')) return;
    await axios.delete(`${API}/notes/${id}`);
    setNotes(prev => prev.filter(n => n.id !== id));
    toast.success('Nota eliminada');
  };

  const openEdit = (note) => {
    setEditNote(note);
    setNewNote({ title: note.title, content: note.content, category: note.category || 'General', color: note.color || COLORS[0], starred: note.starred || false });
    setShowCreate(true);
  };

  const exportNotes = () => {
    const exp = filtered.length > 0 ? filtered : notes;
    if (!exp.length) { toast.info('No hay notas para exportar'); return; }
    let content = `# Mis Notas — OkBot\nFecha: ${new Date().toLocaleDateString('es-MX')}\n\n---\n\n`;
    exp.forEach((n, i) => {
      content += `## ${i + 1}. ${n.title}\n**Categoría:** ${n.category || 'General'}\n**Fecha:** ${new Date(n.created_at).toLocaleDateString('es-MX')}\n\n${n.content}\n\n---\n\n`;
    });
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `notas-okbot-${Date.now()}.md`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    toast.success(`${exp.length} notas exportadas`);
  };

  const filtered = notes
    .filter(n => selectedCategory === 'Todas' || n.category === selectedCategory)
    .filter(n => !search || n.title?.toLowerCase().includes(search.toLowerCase()) || n.content?.toLowerCase().includes(search.toLowerCase()));

  const starredNotes = filtered.filter(n => n.starred);
  const regularNotes = filtered.filter(n => !n.starred);

  return (
      <>
      <SectionAgent section="notes" />
      <div className="p-4 md:p-6 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <StickyNote className="text-yellow-400" size={24} />
            Notas
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">{notes.length} nota{notes.length !== 1 ? 's' : ''} guardada{notes.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={exportNotes} className="btn-secondary flex items-center gap-2 text-sm">
            <Download size={16} />Exportar
          </button>
          <button onClick={() => { setEditNote(null); setNewNote({ title: '', content: '', category: 'General', color: COLORS[0], starred: false }); setShowCreate(true); }}
            className="btn-primary flex items-center gap-2">
            <Plus size={18} />Nueva Nota
          </button>
        </div>
      </div>

      {/* Search + Filter row */}
      <div className="flex gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar notas..."
            className="input-glass pl-9 w-full text-sm"
          />
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2 no-scrollbar">
        {NOTE_CATEGORIES.map(cat => (
          <button key={cat.id} onClick={() => setSelectedCategory(cat.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-all ${
              selectedCategory === cat.id
                ? 'bg-red-500 text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}>
            <span>{cat.emoji}</span>{cat.label}
          </button>
        ))}
      </div>

      {/* Create / Edit Form */}
      {showCreate && (
        <div className="glass-panel p-5 mb-6 animate-fade-in-up">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">{editNote ? 'Editar Nota' : 'Nueva Nota'}</h3>
            <button onClick={() => { setShowCreate(false); setEditNote(null); }} className="p-1 hover:bg-white/10 rounded-lg">
              <X size={18} className="text-gray-400" />
            </button>
          </div>

          {/* Title row */}
          <div className="flex gap-2 mb-3">
            <input
              value={newNote.title}
              onChange={e => setNewNote(p => ({ ...p, title: e.target.value }))}
              placeholder="Título de la nota..."
              className="input-glass flex-1 font-medium"
            />
            <button onClick={() => handleVoice('title')}
              className={`px-3 rounded-xl transition-colors ${listening && listeningField === 'title' ? 'bg-red-500 text-white animate-pulse' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
              title="Dictar título">
              {listening && listeningField === 'title' ? <MicOff size={18} /> : <Mic size={18} />}
            </button>
          </div>

          {/* Content row */}
          <div className="flex gap-2 mb-4">
            <textarea
              value={newNote.content}
              onChange={e => setNewNote(p => ({ ...p, content: e.target.value }))}
              placeholder="Escribe o dicta el contenido..."
              className="input-glass flex-1 h-32 resize-none"
            />
            <button onClick={() => handleVoice('content')}
              className={`px-3 rounded-xl transition-colors self-start ${listening && listeningField === 'content' ? 'bg-red-500 text-white animate-pulse' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
              title="Dictar contenido">
              {listening && listeningField === 'content' ? <MicOff size={18} /> : <Mic size={18} />}
            </button>
          </div>

          {/* Options row */}
          <div className="flex flex-wrap gap-3 mb-4 items-center">
            <select value={newNote.category} onChange={e => setNewNote(p => ({ ...p, category: e.target.value }))}
              className="input-glass text-sm flex-1 min-w-[140px]">
              {NOTE_CATEGORIES.filter(c => c.id !== 'Todas').map(c => (
                <option key={c.id} value={c.id}>{c.emoji} {c.label}</option>
              ))}
            </select>

            <div className="flex items-center gap-2">
              <span className="text-gray-500 text-sm">Color:</span>
              {COLORS.map(c => (
                <button key={c} onClick={() => setNewNote(p => ({ ...p, color: c }))}
                  className={`w-6 h-6 rounded-full border-2 transition-all ${newNote.color === c ? 'border-red-400 scale-125' : 'border-transparent'}`}
                  style={{ backgroundColor: c }} />
              ))}
            </div>

            <button onClick={() => setNewNote(p => ({ ...p, starred: !p.starred }))}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${newNote.starred ? 'bg-yellow-500/20 text-yellow-400' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}>
              <Star size={14} />Destacar
            </button>
          </div>

          <div className="flex gap-3 justify-end">
            <button onClick={() => { setShowCreate(false); setEditNote(null); }} className="btn-secondary text-sm">Cancelar</button>
            <button onClick={saveNote} className="btn-primary text-sm flex items-center gap-2">
              <Check size={16} />{editNote ? 'Actualizar' : 'Guardar'}
            </button>
          </div>
        </div>
      )}

      {/* Starred Notes */}
      {starredNotes.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Star size={14} className="text-yellow-400" />
            <span className="text-yellow-400 text-sm font-medium">Destacadas</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {starredNotes.map(n => <NoteCard key={n.id} note={n} onEdit={openEdit} onDelete={deleteNote} />)}
          </div>
        </div>
      )}

      {/* All Notes */}
      {loading ? (
        <div className="text-center py-16 text-gray-500">Cargando notas...</div>
      ) : regularNotes.length === 0 && starredNotes.length === 0 ? (
        <div className="glass-panel p-16 text-center">
          <StickyNote size={48} className="text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500 mb-2">
            {search ? 'No se encontraron notas' : 'Sin notas en esta categoría'}
          </p>
          {!search && <button onClick={() => setShowCreate(true)} className="btn-primary mt-4 text-sm">Crear primera nota</button>}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {regularNotes.map(n => <NoteCard key={n.id} note={n} onEdit={openEdit} onDelete={deleteNote} />)}
        </div>
      )}

      {/* AI Agent floating button */}
      <AIAgentModule sectionContext="notas" compact={true} placeholder="Ayúdame a organizar mis notas..." />
    </div>
  
      </>
    );
};

const NoteCard = ({ note, onEdit, onDelete }) => {
  const catEmoji = NOTE_CATEGORIES.find(c => c.id === note.category)?.emoji || '📝';
  return (
    <div
      onClick={() => onEdit(note)}
      className="rounded-2xl p-4 cursor-pointer group hover:scale-[1.02] transition-all duration-200"
      style={{ background: note.color || '#1e293b', border: '1px solid rgba(255,255,255,0.07)' }}
    >
      <div className="flex items-start justify-between mb-2 gap-2">
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          {note.starred && <Star size={12} className="text-yellow-400 flex-shrink-0" />}
          <h3 className="text-white font-semibold text-sm truncate">{note.title}</h3>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
          <button
            onClick={e => onDelete(note.id, e)}
            className="p-1 hover:bg-red-500/20 rounded-lg transition-colors">
            <Trash2 size={13} className="text-red-400" />
          </button>
        </div>
      </div>
      <p className="text-gray-400 text-xs line-clamp-3 mb-3 leading-relaxed">{note.content}</p>
      <div className="flex items-center justify-between">
        <span className="text-xs px-2 py-0.5 rounded-full bg-white/5 text-gray-500">{catEmoji} {note.category || 'General'}</span>
        <span className="text-xs text-gray-600">{new Date(note.created_at).toLocaleDateString('es-MX')}</span>
      </div>
    </div>
  );
};

export default NotesModule;
